import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Search, Mail, Phone, Linkedin, User } from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import { ContactModal } from '../components/crm/ContactModal';
import { contactName } from '../components/crm/constants';

export default function SalesContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    const res = await crmFetch('/contacts');
    if (res.tableMissing) setTableMissing(true);
    else if (res.success) { setContacts(res.data || []); setTableMissing(false); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = contacts.filter((c) => {
    if (!q) return true;
    return [contactName(c), c.job_title, c.email, c.account?.name].some((v) => (v || '').toLowerCase().includes(q));
  });

  return (
    <SalesLayout>
      <div data-testid="crm-contacts-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Contacts</h1>
            <p className="text-sm text-gray-500 mt-1">People at your client companies.</p>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setModalOpen(true)} data-testid="new-contact-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Contact
          </Button>
        </div>

        {tableMissing && (
          <Card className="border-amber-300 bg-amber-50" data-testid="crm-migration-notice">
            <CardContent className="p-6 text-sm text-amber-800">
              CRM tables not migrated yet — run <code className="px-1 bg-amber-100 rounded">2026-08-30-crm-phase1.sql</code> in the Supabase SQL Editor.
            </CardContent>
          </Card>
        )}

        {!tableMissing && (
          <>
            <div className="relative max-w-sm mb-4">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input className="pl-9" placeholder="Search contacts…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="contacts-search-input" />
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50/60 text-left text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-4 py-3 font-medium">Name</th>
                        <th className="px-4 py-3 font-medium">Job Title</th>
                        <th className="px-4 py-3 font-medium">Account</th>
                        <th className="px-4 py-3 font-medium">Email</th>
                        <th className="px-4 py-3 font-medium">Phone</th>
                        <th className="px-4 py-3 font-medium">LinkedIn</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>}
                      {!loading && filtered.length === 0 && (
                        <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                          {contacts.length === 0 ? 'No contacts yet — add your first one.' : 'No contacts match your search.'}
                        </td></tr>
                      )}
                      {filtered.map((c) => (
                        <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`contact-row-${c.id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
                                <User className="w-4 h-4" />
                              </div>
                              <span className="font-medium text-gray-900">{contactName(c)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{c.job_title || '—'}</td>
                          <td className="px-4 py-3">
                            {c.account
                              ? <Link to={`/accounts/${c.account.id}`} className="text-blue-600 hover:underline">{c.account.name}</Link>
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {c.email ? <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-blue-600"><Mail className="w-3.5 h-3.5 text-gray-400" />{c.email}</a> : '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {c.phone ? <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-gray-400" />{c.phone}</span> : '—'}
                          </td>
                          <td className="px-4 py-3">
                            {c.linkedin_url ? <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1.5"><Linkedin className="w-3.5 h-3.5" />Profile</a> : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <ContactModal open={modalOpen} onOpenChange={setModalOpen} onSaved={load} />
    </SalesLayout>
  );
}
